// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Card, Grid, Group, Skeleton, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, getDisplayString } from '@medplum/core';
import type {
  Coding,
  Coverage,
  Encounter,
  Extension,
  Observation,
  Patient,
  Practitioner,
  Provenance,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile, useResource } from '@medplum/react';
import { IconLock } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useRole } from '../../hooks/useRole';
import { botIdentifier } from '../../lib/bot-identifier';
import { ChartNoteStatus } from '../../types/encounter';
import { showErrorNotification } from '../../utils/notifications';
import { getTelecom } from '../../utils/patient-contact';
import { getPlanName } from '../../utils/patient-coverage';
import { hasPermission } from '../../utils/permissions';
import { withTenantScope } from '../../utils/tenant';
import noteStyles from '../encounter/EncounterNoteRefactoredShell.module.css';
import { SignAddendum } from '../encounter/SignAddendum';
import { PatientTreatmentsSection } from '../patient/PatientTreatmentsSection';

type Values = {
  chiefComplaint: string;
  goals: string;
  discussion: string;
  noteForPatient: string;
  noteForCareTeam: string;
};
const EMPTY: Values = { chiefComplaint: '', goals: '', discussion: '', noteForPatient: '', noteForCareTeam: '' };
const SYSTEM = 'https://polarissleep.com/fhir/coordinator-note';
const CODES: [keyof Values, string][] = [
  ['chiefComplaint', 'chief-complaint'],
  ['goals', 'goals'],
  ['discussion', 'discussion'],
  ['noteForPatient', 'note-for-patient'],
  ['noteForCareTeam', 'note-for-care-team'],
];

function isCheckIn(encounter: Encounter): boolean {
  return (
    encounter.type?.some((type) =>
      type.coding?.some(
        (coding) => coding.code?.toLowerCase() === 'check-in' || coding.display?.toLowerCase() === 'check-in'
      )
    ) ?? false
  );
}

export function CoordinatorNotePage(): JSX.Element {
  const { patientId, encounterId } = useParams();
  const patient = useResource<Patient>(patientId ? { reference: `Patient/${patientId}` } : undefined);
  const encounter = useResource<Encounter>(encounterId ? { reference: `Encounter/${encounterId}` } : undefined);
  const profile = useMedplumProfile() as Practitioner | undefined;
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { role, isAdmin } = useRole();
  const canWrite = hasPermission({ role, isAdmin, permission: 'author-coordinator-note' });
  const [coverage, setCoverage] = useState<Coverage | undefined>();
  const [observations, setObservations] = useState<Observation[]>([]);
  const [values, setValues] = useState<Values>(EMPTY);
  const [phone, setPhone] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [memberId, setMemberId] = useState('');
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveState, setSaveState] = useState('Draft not saved');
  const [provenances, setProvenances] = useState<Provenance[]>([]);
  const [lockedBy, setLockedBy] = useState('');
  const savingRef = useRef(false);
  const valuesRef = useRef(values);
  const observationsRef = useRef(observations);
  const lastSavedValuesRef = useRef<Values>(EMPTY);
  const patientVersionRef = useRef<string | undefined>(undefined);
  const coverageVersionRef = useRef<string | undefined>(undefined);

  valuesRef.current = values;
  observationsRef.current = observations;

  const load = useCallback(async (): Promise<void> => {
    if (!encounterId) {
      return;
    }
    const result = await medplum.searchResources('Observation', {
      encounter: `Encounter/${encounterId}`,
      code: CODES.map(([, code]) => `${SYSTEM}|${code}`).join(','),
    });
    setObservations(result);
    const next = { ...EMPTY };
    for (const [key, code] of CODES) {
      next[key] =
        result.find((obs) =>
          obs.code?.coding?.some((coding: Coding) => coding.system === SYSTEM && coding.code === code)
        )?.valueString ?? '';
    }
    setValues(next);
    lastSavedValuesRef.current = next;
    const isLocked = result.some((obs) =>
      obs.extension?.some((ext: Extension) => ext.url === `${SYSTEM}/locked` && ext.valueBoolean === true)
    );
    setLocked(isLocked);
    const targets = result
      .filter((obs) => obs.id)
      .map((obs) => `Observation/${obs.id}`)
      .join(',');
    const loadedProvenances = targets ? await medplum.searchResources('Provenance', { target: targets }) : [];
    setProvenances(loadedProvenances);
    const lockProvenance = loadedProvenances.find((provenance) =>
      provenance.activity?.coding?.some((coding: Coding) => coding.code === 'lock')
    );
    setLockedBy(lockProvenance?.agent?.[0]?.who?.display ?? 'Coordinator');
    if (isLocked) {
      setSaveState(`Locked · ${dayjs(lockProvenance?.recorded ?? result[0]?.meta?.lastUpdated).format('MMM D, YYYY')}`);
    }
  }, [encounterId, medplum]);
  useEffect(() => {
    load().catch(showErrorNotification);
  }, [load]);
  useEffect(() => {
    if (patient) {
      setPhone(getTelecom(patient, 'phone'));
      patientVersionRef.current = patient.meta?.versionId;
    }
  }, [patient]);
  useEffect(() => {
    if (patient?.id) {
      medplum
        .searchOne('Coverage', { beneficiary: `Patient/${patient.id}`, _sort: '-_lastUpdated' })
        .then((next) => {
          setCoverage(next);
          coverageVersionRef.current = next?.meta?.versionId;
          if (next) {
            setInsuranceType(getPlanName(next));
            setMemberId(next.subscriberId ?? '');
          }
        })
        .catch(showErrorNotification);
    }
  }, [medplum, patient?.id]);

  const save = useCallback(
    async (lock = false): Promise<void> => {
      if (!patient || !encounter || !canWrite || savingRef.current) {
        return;
      }
      if (!isCheckIn(encounter)) {
        return;
      }
      const currentValues = valuesRef.current;
      if (
        lock &&
        (!currentValues.chiefComplaint.trim() || !currentValues.goals.trim() || !currentValues.discussion.trim())
      ) {
        notifications.show({ color: 'red', message: 'Chief Complaint, Goals, and Discussion are required.' });
        return;
      }
      if (!lock && CODES.every(([key]) => currentValues[key] === lastSavedValuesRef.current[key])) {
        return;
      }
      savingRef.current = true;
      setSaving(true);
      try {
        const nextObservations: Observation[] = [];
        for (const [key, code] of CODES) {
          const value = currentValues[key].trim();
          const existing = observationsRef.current.find((obs) =>
            obs.code?.coding?.some((coding) => coding.system === SYSTEM && coding.code === code)
          );
          if (!value && !existing) {
            continue;
          }
          if (!value && existing?.id) {
            await medplum.deleteResource('Observation', existing.id);
            continue;
          }
          const next = withTenantScope<Observation>({
            ...(existing ?? {}),
            resourceType: 'Observation',
            status: 'final',
            subject: { reference: `Patient/${patient.id}` },
            encounter: { reference: `Encounter/${encounter.id}` },
            performer: profile ? [createReference(profile)] : undefined,
            code: { coding: [{ system: SYSTEM, code }] },
            ...(value ? { valueString: value } : {}),
            issued: new Date().toISOString(),
            ...(lock ? { extension: [{ url: `${SYSTEM}/locked`, valueBoolean: true }] } : {}),
          });
          nextObservations.push(
            existing?.id
              ? await medplum.updateResource({ ...next, id: existing.id })
              : await medplum.createResource(next)
          );
        }
        if (lock && nextObservations.length) {
          const lockProvenance = await medplum.createResource(
            withTenantScope<Provenance>({
              resourceType: 'Provenance',
              target: nextObservations.filter((obs) => obs.id).map((obs) => ({ reference: `Observation/${obs.id}` })),
              recorded: new Date().toISOString(),
              agent: profile ? [{ who: createReference(profile) }] : [],
              activity: { coding: [{ system: SYSTEM, code: 'lock' }] },
            })
          );
          setProvenances((current) => [...current, lockProvenance]);
          setLockedBy(profile ? getDisplayString(profile) : 'Coordinator');
        }
        setObservations(nextObservations);
        lastSavedValuesRef.current = currentValues;
        setLocked(lock);
        setSaveState(
          lock
            ? `Locked · ${dayjs().format('MMM D, YYYY')}`
            : `${Object.values(currentValues).join('').trim().length} characters · draft saved`
        );
        notifications.show({
          color: 'green',
          message: lock ? 'Coordinator note saved and locked.' : 'Coordinator note saved.',
        });
      } catch (error) {
        setSaveState('Save failed — your text is still here');
        showErrorNotification(error);
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [canWrite, encounter, medplum, patient, profile]
  );
  const savePatientInfo = async (): Promise<void> => {
    if (!patient || locked || !canWrite) {
      return;
    }
    const result = (await medplum.executeBot(botIdentifier('update-coordinator-patient-info'), {
      patientId: patient.id,
      coverageId: coverage?.id,
      phone,
      insuranceType,
      memberId,
      expectedPatientVersionId: patientVersionRef.current,
      expectedCoverageVersionId: coverageVersionRef.current,
    })) as { patient?: Patient; coverage?: Coverage };
    patientVersionRef.current = result.patient?.meta?.versionId ?? patientVersionRef.current;
    coverageVersionRef.current = result.coverage?.meta?.versionId ?? coverageVersionRef.current;
    notifications.show({ color: 'green', message: 'Patient information saved.' });
  };
  const deleteDraft = async (): Promise<void> => {
    if (!patient || locked || !canWrite || deleting) {
      return;
    }
    if (!window.confirm('Delete this draft coordinator note? This cannot be undone.')) {
      return;
    }
    setDeleting(true);
    try {
      await medplum.createResource(
        withTenantScope<Provenance>({
          resourceType: 'Provenance',
          target: observations
            .filter((observation) => observation.id)
            .map((observation) => ({ reference: `Observation/${observation.id}` })),
          recorded: new Date().toISOString(),
          agent: profile ? [{ who: createReference(profile) }] : [],
          activity: { coding: [{ system: SYSTEM, code: 'delete-draft' }] },
        })
      );
      await Promise.all(
        observations
          .filter((observation) => observation.id)
          .map((observation) => medplum.deleteResource('Observation', observation.id as string))
      );
      setObservations([]);
      setValues(EMPTY);
      notifications.show({ color: 'green', message: 'Coordinator note draft deleted.' });
      navigate(`/Patient/${patient.id}/medical-info`);
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setDeleting(false);
    }
  };
  const count = useMemo(() => Object.values(values).join('').trim().length, [values]);
  if (!patient || !encounter) {
    return (
      <Stack gap="lg">
        <Skeleton height={32} width="55%" />
        <Skeleton height={110} />
        <Skeleton height={320} />
      </Stack>
    );
  }
  if (!isCheckIn(encounter)) {
    return <Alert color="gray">Coordinator Notes are available only for Check-in encounters.</Alert>;
  }
  const update = (key: keyof Values, value: string): void => setValues((current) => ({ ...current, [key]: value }));
  return (
    <Stack gap="lg">
      <div className={noteStyles.noteHeader}>
        <div className={noteStyles.noteHeaderTitleRow}>
          <Title c="white" order={2}>
            Coordinator Note <span className={noteStyles.noteVersion}>{locked ? '(v.1)' : '(draft)'}</span>
          </Title>
          <span
            className={`${noteStyles.noteStatusPill} ${locked ? noteStyles.noteStatusPill_locked : noteStyles.noteStatusPill_draft}`}
          >
            {locked ? 'Signed & Locked' : 'Draft'}
          </span>
        </div>
        {canWrite && (
          <Group gap="sm">
            <Button
              className={noteStyles.noteHeaderGhostButton}
              color="red"
              disabled={locked}
              onClick={() => deleteDraft().catch(showErrorNotification)}
              loading={deleting}
            >
              Delete Note
            </Button>
            <Button
              className={noteStyles.noteHeaderGhostButton}
              disabled={locked}
              onClick={() => save(false)}
              loading={saving}
            >
              Save as Draft
            </Button>
            <Button
              className={noteStyles.noteHeaderPrimaryButton}
              disabled={locked}
              onClick={() => save(true)}
              loading={saving}
              leftSection={<IconLock size={16} />}
            >
              Save &amp; Lock
            </Button>
          </Group>
        )}
      </div>
      <Stack gap="lg" p="xl">
        {locked && (
          <Alert icon={<IconLock size={16} />} role="status">
            Saved &amp; locked by {lockedBy || 'Coordinator'} — this note is final and on the chart.
          </Alert>
        )}
        <Card withBorder radius="lg" p="lg">
          <Group justify="space-between" mb="md">
            <Title order={3}>Patient Information</Title>
            <Text size="sm" c="dimmed">
              Editable · writes back to the patient chart
            </Text>
          </Group>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <TextInput label="Email" value={getTelecom(patient, 'email')} readOnly />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <TextInput
                label="Phone Number"
                value={phone}
                readOnly={locked || !canWrite}
                onChange={(event) => setPhone(event.currentTarget.value)}
                onBlur={() => savePatientInfo().catch(showErrorNotification)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <TextInput
                label="Insurance Type"
                value={insuranceType}
                readOnly={locked || !canWrite}
                onChange={(event) => setInsuranceType(event.currentTarget.value)}
                onBlur={() => savePatientInfo().catch(showErrorNotification)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <TextInput
                label="Member ID"
                value={memberId}
                readOnly={locked || !canWrite}
                onChange={(event) => setMemberId(event.currentTarget.value)}
                onBlur={() => savePatientInfo().catch(showErrorNotification)}
              />
            </Grid.Col>
          </Grid>
        </Card>
        <PatientTreatmentsSection patient={patient} place="note" />
        <Card withBorder radius="lg" p="lg">
          <Group justify="space-between" mb="md">
            <Title order={3}>Coordinator Note</Title>
            <Text size="sm" c="dimmed">
              Save drafts manually
            </Text>
          </Group>
          <Stack>
            <Textarea
              label="Chief Complaint"
              placeholder="What the patient says brought them in, in their words."
              required
              readOnly={locked || !canWrite}
              value={values.chiefComplaint}
              onChange={(event) => update('chiefComplaint', event.currentTarget.value)}
            />
            <Textarea
              label="Goals"
              placeholder="What the patient wants out of treatment, and what this visit should accomplish."
              required
              readOnly={locked || !canWrite}
              value={values.goals}
              onChange={(event) => update('goals', event.currentTarget.value)}
            />
            <Textarea
              label="Discussion"
              placeholder="What was covered on the call — patient-reported changes, barriers, questions to raise with the clinician."
              required
              readOnly={locked || !canWrite}
              value={values.discussion}
              onChange={(event) => update('discussion', event.currentTarget.value)}
            />
            <Textarea
              label="Note for Patient (optional) · Patient-visible after lock"
              placeholder="Written for the patient. Visible to them in the portal."
              readOnly={locked || !canWrite}
              value={values.noteForPatient}
              onChange={(event) => update('noteForPatient', event.currentTarget.value)}
            />
            <Textarea
              label="Note for Care Team (optional) · Internal only"
              placeholder="Internal only. Anything the clinician should know before the visit."
              readOnly={locked || !canWrite}
              value={values.noteForCareTeam}
              onChange={(event) => update('noteForCareTeam', event.currentTarget.value)}
            />
          </Stack>
          <Text size="sm" c="dimmed" mt="md">
            Coordination note only. It is not signed, not billed, and does not replace the encounter note.
          </Text>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">
              {profile ? getDisplayString(profile) : 'Coordinator'} · {dayjs().format('MMM D, YYYY')}
            </Text>
            <Text size="sm" c="dimmed">
              {saveState || `${count} characters · saves as you type`}
            </Text>
          </Group>
        </Card>
        {locked && (
          <SignAddendum
            provenances={provenances}
            chartNoteStatus={ChartNoteStatus.SignedAndLocked}
            encounter={encounter}
          />
        )}
        <Group className={noteStyles.footBar}>
          {!locked && (
            <Button
              color="red"
              variant="default"
              onClick={() => deleteDraft().catch(showErrorNotification)}
              loading={deleting}
            >
              Delete Note
            </Button>
          )}
          {!locked && (
            <Button variant="default" onClick={() => save(false)} loading={saving}>
              Save as Draft
            </Button>
          )}
          {!locked && (
            <Button onClick={() => save(true)} loading={saving} leftSection={<IconLock size={16} />}>
              Save &amp; Lock
            </Button>
          )}
        </Group>
      </Stack>
    </Stack>
  );
}
